// SIGN-OFF TIMELINE (back/routes/signoff.js, back/routes/signoffPublic.js).
//
// The element-by-element reads the 3D viewer and the tile picker used (/tiles/elements,
// /requests/elements, /tiles/:key/elements, /model-points) are not called from here any
// more: the drawing is the isometric, which needs only the tile set. The routes stay -
// they are read-only, and /requests/elements is what the request itself records its
// geometry from on the server.
import { url, tokenRef } from "api";

async function json(method, path, body, { auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) headers.Authorization = `Bearer ${localStorage.getItem(tokenRef)}`;
  const r = await fetch(`${url}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(out.err || out.error || `Request failed (${r.status})`), { status: r.status });
  return out;
}

export const getSignoffTiles = (projectId) => json("GET", `/projects/${projectId}/signoff/tiles`);
export const getSignoffTimeline = (projectId) => json("GET", `/projects/${projectId}/signoff/timeline`);
export const getSignoffRecipients = (projectId) => json("GET", `/projects/${projectId}/signoff/recipients`);
export const getSignoffContacts = (projectId) => json("GET", `/projects/${projectId}/signoff/contacts`);
export const getGridLines = (projectId) => json("GET", `/projects/${projectId}/signoff/grid-lines`);
export const previewSignoffRequest = (projectId, targets) => json("POST", `/projects/${projectId}/signoff/requests/preview`, { targets });
export const sendSignoffRequest = (projectId, body) => json("POST", `/projects/${projectId}/signoff/requests`, body);
export const revokeSignoffLink = (projectId, linkId) => json("POST", `/projects/${projectId}/signoff/links/${linkId}/revoke`);
export const resendSignoffLink = (projectId, linkId) => json("POST", `/projects/${projectId}/signoff/links/${linkId}/resend`);

// the emailed link: no login
export const getSignoffLink = (token) => json("GET", `/signoff-links/${token}`, null, { auth: false });
export const signOffLink = (token) => json("POST", `/signoff-links/${token}/signoff`, { confirm: true }, { auth: false });
export const requestChangesLink = (token, comment) => json("POST", `/signoff-links/${token}/changes`, { comment }, { auth: false });
export const askNewLink = (token) => json("POST", `/signoff-links/${token}/renew`, null, { auth: false });

async function save(path, fallbackName) {
  const r = await fetch(`${url}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
  if (!r.ok) {
    const out = await r.json().catch(() => ({}));
    throw new Error(out.err || `Could not make the file (${r.status})`);
  }
  const blob = await r.blob();
  // the real name (filename*, UTF-8) first; the plain ASCII one for an older server
  const cd = r.headers.get("Content-Disposition") || "";
  const utf = (/filename\*=UTF-8''([^;]+)/i.exec(cd) || [])[1];
  const name = (utf && decodeURIComponent(utf)) || (/filename="([^"]+)"/.exec(cd) || [])[1] || fallbackName;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
}

/** The sign-off certificate PDF of one ring, saved by the browser. */
/** The sign-off print: every sign-off and request in full, or one date's (day YYYY-MM-DD). */
export const downloadSignoffPrint = (projectId, day = "") =>
  // tz: the browser's offset, so "one day" is the day the timeline shows
  save(`/projects/${projectId}/signoff/print.pdf${day ? `?day=${day}&tz=${new Date().getTimezoneOffset()}` : ""}`, day ? `Sign-offs ${day}.pdf` : "Sign-off history.pdf");
export const downloadSignoffSnapshot = (projectId, ringId) =>
  save(`/projects/${projectId}/signoff/rings/${encodeURIComponent(ringId)}/snapshot.pdf`, "Sign-off certificate.pdf");

/** One of a request's rendered views, as an object URL the caller revokes. */
export async function requestViewUrl(projectId, requestId, grant, kind) {
  const r = await fetch(`${url}/projects/${projectId}/signoff/requests/${requestId}/views/${grant}/${kind}`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
  if (!r.ok) return "";
  return URL.createObjectURL(await r.blob());
}
/** An export ring's Changes.xlsx. */
export const downloadExportChanges = (projectId, versionId) =>
  save(`/projects/${projectId}/signoff/exports/${versionId}/changes.xlsx`, "Changes.xlsx");
export const getExportChanges = (projectId, versionId) => json("GET", `/projects/${projectId}/signoff/exports/${versionId}/changes`);

/** A model revision's star, given (or withdrawn) by the signed-in person. */
export const signRevision = (projectId, versionId, star, withdraw = false) =>
  json("POST", `/projects/${projectId}/signoff/revisions/${versionId}/signoff`, { star, withdraw });

/** The project's Project at a Glance manifest - the isometric's fine geometry, when it has one. */
export const getGlanceManifest = (projectId) => json("GET", `/projects/${projectId}/glance`);
